import { applyNodeChanges, NodeChange, XYPosition } from "@xyflow/react";
import {
  DocumentState,
  DocumentStateBase,
} from "@/services/backend/documents/types/state";
import { v4 } from "uuid";
import {
  Item,
  SizeFactor,
} from "@/services/backend/htz/ahu/types/ahu-component-catalog";
import { UUID } from "@/shared/types/entity-id";
import { RFC3339timestamp } from "@bitory-gmbh/nidavellir";
import { DocumentOwnership } from "@bitory-gmbh/bsm";

export type AirHandlingUnitData = DocumentStateBase &
  DocumentOwnership & {
    id: UUID;
    airHandlingUnitId: UUID;
    companyId: UUID;
    customerId: UUID;
    serviceObjectId: UUID;
    label: string;
    designation: string;
    location: string;
    constructionYear: string;
    volumeFlowRate: string;
    components: ComponentData[];
    createdAt: RFC3339timestamp;
    updatedAt: RFC3339timestamp;
  };

/**
 * copyAirHandlingUnitData creates a deep copy of an existing AirHandlingUnitData,
 * mirroring the backend Data.Copy() behavior. All identifying IDs are reset to
 * new values. The label is appended with "-copy" and the state is reset.
 */
export function copyAirHandlingUnitData(
  source: AirHandlingUnitData,
  ownerId: UUID,
  ownerType: string,
): AirHandlingUnitData {
  const now = new Date().toISOString();
  const newDataId = v4();
  return {
    ...source,
    id: newDataId,
    label: `${ownerType}-${ownerId}`,
    state: DocumentState.Draft,
    ownerId,
    ownerType,
    createdAt: now,
    updatedAt: now,
    components: source.components.map((c) => ({
      ...c,
      id: v4(),
    })),
  };
}

/**
 * addComponentDataFromTemplate adds a component (data) to the ahuData.
 *
 * @param ahuData is the AirHandlingUnitData to add the component to.
 * @param componentId is the ID of the general component the component data belongs to.
 * @param template is the data to copy from.
 */
export function addComponentDataFromTemplate(
  ahuData: AirHandlingUnitData,
  componentId: UUID,
  template: Item,
): AirHandlingUnitData {
  const componentData = newFromTemplate(
    template,
    componentId,
    ahuData.id,
    // add it to the end by default
    ahuData.components.length,
  );

  return {
    ...ahuData,
    components: [...ahuData.components, componentData],
  };
}

export function newFromTemplate(
  template: Item,
  componentId: UUID,
  ahuDataId: UUID,
  order: number,
): ComponentData {
  const id = v4();
  return {
    id,
    componentId,
    ahuDataId,
    name: template.name,
    details: template.details,
    componentKindId: template.componentKindId,
    order,
    type: template.type,
    position: { x: 0, y: 0 },
    data: {
      name: template.name,
      img: template.img,
      sizeFactor: template.sizeFactor,
      details: template.details,
    },
  };
}

export function removeComponent(
  ahu: AirHandlingUnitData,
  id: string,
): AirHandlingUnitData {
  return {
    ...ahu,
    components: ahu.components.filter((component) => component.id !== id),
  };
}

/**
 * changeNodes enables integrating an AirHandlingUnit with
 * React Flow. It can be used in event handlers passed to
 * a ReactFlow component.
 * @param ahu
 * @param changes
 */
export function changeNodes(
  ahu: AirHandlingUnitData,
  changes: NodeChange<ComponentData>[],
): AirHandlingUnitData {
  return {
    ...ahu,
    components: applyNodeChanges(changes, ahu.components),
  };
}

export function moveUp(
  ahu: AirHandlingUnitData,
  component: ComponentData,
): AirHandlingUnitData {
  if (component.order === 0) {
    return ahu;
  }

  const components = ahu.components.map((c, order) => ({ ...c, order }));

  const beforeAfter = components.reduce(
    (prev, cur) => {
      if (cur.id === component.id) {
        return prev;
      }
      if (cur.order < component.order - 1) {
        return {
          ...prev,
          before: [...prev.before, cur],
        };
      }
      return {
        ...prev,
        after: [...prev.after, cur],
      };
    },
    { before: [] as ComponentData[], after: [] as ComponentData[] },
  );

  const { before, after } = beforeAfter;

  return {
    ...ahu,
    components: [...before, component, ...after].map((c, order) => ({
      ...c,
      order,
    })),
  };
}

export function moveDown(
  ahu: AirHandlingUnitData,
  component: ComponentData,
): AirHandlingUnitData {
  const components = ahu.components.map((c, order) => ({ ...c, order }));

  const beforeAfter = components.reduce(
    (prev, cur) => {
      if (cur.id === component.id) {
        return prev;
      }
      if (cur.order <= component.order + 1) {
        return {
          ...prev,
          before: [...prev.before, cur],
        };
      }
      return {
        ...prev,
        after: [...prev.after, cur],
      };
    },
    { before: [] as ComponentData[], after: [] as ComponentData[] },
  );

  const { before, after } = beforeAfter;

  return {
    ...ahu,
    components: [...before, component, ...after].map((c, order) => ({
      ...c,
      order,
    })),
  };
}

// persistedShape is everything of the ahu data the backend stores. The
// schema also reports the measurements and the selection of its nodes,
// which are render state and must not trigger a save.
export function persistedShape(ahuData: AirHandlingUnitData): string {
  return JSON.stringify({
    label: ahuData.label,
    designation: ahuData.designation,
    location: ahuData.location,
    constructionYear: ahuData.constructionYear,
    volumeFlowRate: ahuData.volumeFlowRate,
    components: ahuData.components.map((component) => ({
      id: component.id,
      componentId: component.componentId,
      name: component.name,
      details: component.details,
      componentKindId: component.componentKindId,
      order: component.order,
      type: component.type,
      position: component.position,
    })),
  });
}

/**
 * A Component represents a building block of an air handling unit.
 * This could be filter chambers, cross flow heat exchangers or similar.
 *
 * Component is designed such that it can be passed as a Node into
 * a ReactFlow diagram. While this design strongly couples the
 * implementation of a component to ReactFlow and vice versa, it was
 * the simplest way to integrate both at the time. The coupling
 * in trade-off to more complex designs was willingly accepted.
 */
export interface ComponentData {
  // The component id is also used as a ReactFlow id.
  id: string;
  componentId: UUID;
  ahuDataId: UUID;
  name: string;
  details: string;
  componentKindId: UUID;
  // Order allows to order the components in the table view.
  // This is helpful, since there actually is a partial order
  // among the components defined by the direction the air is
  // flowing through them.
  order: number;
  // The following are ReactFlow specific fields
  // to make Component satisfy the NodeBase constraint.
  type: string;
  position: XYPosition;
  // This data is passed into ReactFlow components by ReactFlow.
  data: {
    name: string;
    img: string;
    sizeFactor: SizeFactor;
    details: string;
  };
}
